import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import callPiggybackAPI from "../Helpers/piggyback.api.helper";
import { useSelector } from "react-redux";
import ConfirmRefreshModal from "./ConfirmRefreshModal";

function TrunInkEditForm({ open, onClose, data, schema, title, onSubmit }) {
  const [formData, setFormData] = useState(data || {});
  const [askRefresh, setAskRefresh] = useState(false);
  const [iconOptions, setIconOptions] = useState([]);
  const selectedSite = useSelector((state) => state.truInk.selectedSite);

  useEffect(() => {
    setFormData(data || {});
  }, [data]);

  useEffect(() => {
    const hasIconsField = Object.values(schema || {}).some(
      (f) => f.type === "multiselect-icons"
    );
    if (!hasIconsField || !open || !selectedSite) return;
    callPiggybackAPI(
      "labels",
      { tenantId: selectedSite.tenantId, siteId: selectedSite.id },
      {},
      "GET"
    )
      .then((res) => setIconOptions(Array.isArray(res) ? res : []))
      .catch(() => setIconOptions([]));
  }, [open, schema, selectedSite]);

  const handleRefreshChoice = async (refresh) => {
    await onSubmit(formData);
    if (refresh) {
      await callPiggybackAPI(
        "brush/image",
        {},
        {
          siteId: selectedSite.id,
          tenantId: selectedSite.tenantId,
          modifiedValue: "data",
          dynamicDataId: data.id,
        },
        "POST"
      );
    }
    setAskRefresh(false);
    onClose();
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (title?.includes("SiteData")) {
      setAskRefresh(true);
    } else {
      onSubmit(formData);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 3,
          width:"95%",
          maxWidth:400,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {!askRefresh ? (
          <>
            <h3>{title || "Edit Record"}</h3>
            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {Object.keys(schema || {}).map((key) => {
                const fieldSchema = schema[key];
                const value = formData[key] ?? "";

                if (!fieldSchema) return null;
                if (fieldSchema.type === "boolean") {
                  return (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          name={key}
                          checked={!!value}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label={fieldSchema.label}
                    />
                  );
                }

                if (fieldSchema.type === "select") {
                  return (
                    <TextField
                      key={key}
                      select
                      name={key}
                      label={fieldSchema.label}
                      value={value}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      required={fieldSchema.required}
                    >
                      <MenuItem value="">-- Select --</MenuItem>
                      {fieldSchema.options?.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }

                if (fieldSchema.type === "multiselect-icons") {
                  const selectedNames = Array.isArray(formData[key])
                    ? formData[key]
                    : typeof formData[key] === "string" && formData[key]
                    ? formData[key].split(", ")
                    : [];
                  return (
                    <FormControl key={key} fullWidth size="small" sx={{ mt: 2 }}>
                      <InputLabel>{fieldSchema.label}</InputLabel>
                      <Select
                        multiple
                        value={selectedNames}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                        label={fieldSchema.label}
                      >
                        {iconOptions.map((icon) => (
                          <MenuItem key={icon.id} value={icon.name}>{icon.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }

                return (
                  <TextField
                    key={key}
                    name={key}
                    label={fieldSchema.label}
                    value={value}
                    onChange={handleChange}
                    type={fieldSchema.type}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required={fieldSchema.required}
                  />
                );
              })}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  onClick={onClose}
                  variant="contained"
                  sx={{
                    mr: 1,
                    textTransform: "none",
                    backgroundColor: "rgb(90, 28, 47)",
                    "&:hover": { backgroundColor: "rgb(70, 20, 35)" },
                    "&:disabled": { backgroundColor: "rgba(90, 28, 47, 0.4)" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))",
                    "&:hover": { background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))" },
                    "&:disabled": { background: "linear-gradient(90deg, rgb(58,130,150), rgb(63,88,111))" },
                  }}
                >
                  Save
                </Button>
              </Box>
            </form>
          </>
        ) : (
          <>
            <ConfirmRefreshModal
              open={askRefresh}
              onClose={() => handleRefreshChoice(false)}
              onConfirm={() => handleRefreshChoice(true)}
            />
          </>
        )}
      </Box>
    </Modal>
  );
}

export default TrunInkEditForm;
